import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CreateTestDto,
  Test,
  TestReviewResult,
  UpdateTestDto,
} from '../interfaces/test';
import {
  ApiCollectionResponse,
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../interfaces/responses/api-response';
import { Theme } from '../interfaces/theme';
import { HandleErrorsService } from './utils/handle-errors.service';

// DTOs for specific endpoints
export interface AutoGenerateTestRequestDto {
  studentId: string;
  learningPathId: number;
  questionCount?: number;
  themeIds?: number[];
  subjectId?: number;
  timeLimitMinutes?: number;
  passingPercentage?: number;
}

export interface AutoGenerateTestResponseDto {
  testId: number;
  message: string;
  test: Test;
  questionsGenerated: number;
}

export interface TestScoreResultDto {
  sessionId: number;
  score: number;
  percentage: number;
  passed: boolean;
  correctAnswers: number;
  wrongAnswers: number;
  totalQuestions: number;
}

export interface StudentTestProgressionDto {
  studentId: string;
  totalTestsTaken: number;
  totalTestsPassed: number;
  totalTestsFailed: number;
  averageScore: number;
  averagePercentage: number;
  testsByMode: {
    mode: string;
    count: number;
    averageScore: number;
  }[];
}


@Injectable({
  providedIn: 'root',
})
export class TestService {
  // Base API endpoints
  private baseUrl = `${environment.BASE_URL}`;
  private studentTestsUrl = `${environment.BASE_URL}/student-tests`;

  // Subjects for state management
  private testsSubject = new BehaviorSubject<Test[]>([]);
  public tests$ = this.testsSubject.asObservable();

  private testWithQuestionsSubject = new BehaviorSubject<Test | null>(null);
  testWithQuestions$ = this.testWithQuestionsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private errorHandler: HandleErrorsService,
  ) {}

  // ==================== PROFESSOR ENDPOINTS ====================

  /**
   * Get all tests (Admin only)
   * @returns Observable of API collection response with all tests
   */
  getAllTests(): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(`${this.baseUrl}/admin/tests`);
  }

  /**
   * Get all tests by mode (Admin)
   * @param testMode - The test mode (exam, evaluation, practice)
   * @returns Observable of API collection response
   */
  getAllTestsByMode(testMode: string): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/admin/tests/mode/${testMode}`,
    );
  }

  /**
   * Get all tests by mode and date range (Admin)
   * @param testMode - The test mode
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  getAllTestsByModeAndDateRange(
    testMode: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/admin/tests/mode/${testMode}/date-range`,
      { params },
    );
  }

  /**
   * Get all tests by date range (Admin)
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  getAllTestsByDateRange(
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/admin/tests/date-range`,
      { params },
    );
  }

  /**
   * Get all tests by professor ID
   * @param professorId - Professor user ID (GUID)
   * @returns Observable of API collection response with Test array
   */
  /** Get all tests for the authenticated professor (auto-scope via JWT). */
  getByTestsProfessorId(
    _userId?: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests`,
    );
  }

  /**
   * Get all tests by professor and subject ID
   * @param professorId - Professor user ID (GUID)
   * @param subjectId - Subject ID
   * @returns Observable of API collection response with Test array
   */
  getByTestsProfessorSubject(
    userId: string,
    subjectId: number,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/by-subject/${subjectId}`,
    );
  }

  /**
   * Get tests by professor and mode
   * @param professorId - Professor user ID
   * @param testMode - Test mode (exam, evaluation, practice)
   * @returns Observable of API collection response
   */
  /** Tests by mode for the authenticated professor (JWT auto-scope). */
  getTestsByProfessorAndMode(
    _professorId: string,
    testMode: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/mode/${testMode}`,
    );
  }

  /**
   * Get tests by professor, mode, and date range
   * @param professorId - Professor user ID
   * @param testMode - Test mode
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  /** Tests by mode + date range for the authenticated professor (JWT auto-scope). */
  getTestsByProfessorModeAndDateRange(
    _professorId: string,
    testMode: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/mode/${testMode}/date-range`,
      { params },
    );
  }

  /**
   * Get a single Test by ID (scope professeur)
   * @param testId - Test ID
   * @returns Observable of API single response with Test
   */
  getTestById(testId: number): Observable<ApiSingleResponse<Test>> {
    return this.http.get<ApiSingleResponse<Test>>(
      `${this.baseUrl}/professor/tests/${testId}`,
    );
  }

  /**
   * ✅ CRITICAL: Get a test with all questions and answers from Question Bank
   * This endpoint MUST return the complete nested structure:
   * Test → TestQuestions → Question (QuestionBank) → AnswerBanks
   *
   * @param testId - Test ID
   * @returns Observable of API single response with Test including full Question Bank data
   */
  getTestWithQuestions(testId: number): Observable<ApiSingleResponse<Test>> {
    return this.http.get<ApiSingleResponse<Test>>(
      `${this.baseUrl}/professor/tests/${testId}/questions`,
    );
  }

    /**
   * ✅ CRITICAL: Get a test with all questions and answers from Question Bank
   * This endpoint MUST return the complete nested structure:
   * Test → TestQuestions → Question (QuestionBank) → AnswerBanks
   *
   * @param testId - Test ID
   * @returns Observable of API single response with Test including full Question Bank data
   */
  getStudentTestWithQuestions(testId: number): Observable<ApiSingleResponse<Test>> {
    return this.http.get<ApiSingleResponse<Test>>(
      `${this.studentTestsUrl}/${testId}/questions`,
    );
  }

  /**
   * Get tests by subject ID
   * @param subjectId - Subject ID
   * @returns Observable of API collection response
   */
  getTestsBySubject(
    subjectId: number,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/by-subject/${subjectId}`,
    );
  }

  /**
   * Get tests by learning path ID
   * @param learningPathId - Learning path ID
   * @returns Observable of API collection response
   */
  getTestsByLearningPath(
    learningPathId: number,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/by-learning-path/${learningPathId}`,
    );
  }

  /**
   * Get tests by theme ID
   * @param themeId - Theme ID
   * @returns Observable of API collection response
   */
  getTestsByTheme(themeId: number): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/by-theme/${themeId}`,
    );
  }

  /**
   * Get active tests by learning path
   * @param learningPathId - Learning path ID
   * @returns Observable of API collection response with active tests
   */
  getActiveTestsByLearningPath(
    learningPathId: number,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/active/by-learning-path/${learningPathId}`,
    );
  }

  /**
   * Get tests by mode
   * @param testMode - Test mode (exam, evaluation, practice)
   * @returns Observable of API collection response
   */
  getTestsByTestMode(
    testMode: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/mode/${testMode}`,
    );
  }

  /**
   * Get tests by mode and date range
   * @param testMode - Test mode
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  getTestsByModeAndDateRange(
    testMode: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/mode/${testMode}/date-range`,
      { params },
    );
  }

  /**
   * Create a new Test
   * @param payload - CreateTestDto
   * @returns Observable of API single response with created Test
   */
  createTest(payload: CreateTestDto): Observable<ApiSingleResponse<Test>> {
    return this.http.post<ApiSingleResponse<Test>>(`${this.baseUrl}/professor/tests`, payload);
  }

  /**
   * Update an existing Test
   * @param testId - Test ID
   * @param updateDto - UpdateTestDto
   * @returns Observable of API single response with updated Test
   */
  update(
    testId: number,
    updateDto: UpdateTestDto,
  ): Observable<ApiSingleResponse<Test>> {
    return this.http.put<ApiSingleResponse<Test>>(
      `${this.baseUrl}/professor/tests/${testId}`,
      updateDto,
    );
  }

  /**
   * Delete a Test
   * @param id - Test ID
   * @returns Observable of API simple response with status message
   */
  delete(id: number): Observable<ApiSimpleResponse> {
    return this.http.delete<ApiSimpleResponse>(`${this.baseUrl}/professor/tests/${id}`);
  }

  /**
   * Generate automatic evaluation test for student
   * @param request - Auto generate test request DTO
   * @returns Observable with generated test response
   */
  generateEvaluationTest(
    request: AutoGenerateTestRequestDto,
  ): Observable<ApiSingleResponse<AutoGenerateTestResponseDto>> {
    return this.http.post<ApiSingleResponse<AutoGenerateTestResponseDto>>(
      `${this.baseUrl}/professor/tests/generate-evaluation`,
      request,
    );
  }

  // ==================== STUDENT ENDPOINTS ====================

  /**
   * Get all tests available for a student
   * @param studentId - Student user ID (GUID)
   * @returns Observable of API collection response with available tests
   */
  getTestsByStudent(
    studentId: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/student/${studentId}`,
    );
  }

  /**
   * Get available tests for a student filtered by mode
   * @param studentId - Student user ID
   * @param testMode - Test mode (exam, evaluation, practice)
   * @returns Observable of API collection response
   */
  getTestsByStudentAndMode(
    studentId: string,
    testMode: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/student/${studentId}/mode/${testMode}`,
    );
  }

  /**
   * Get tests by student, mode, and date range
   * @param studentId - Student user ID
   * @param testMode - Test mode
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  getTestsByStudentModeAndDateRange(
    studentId: string,
    testMode: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/student/${studentId}/mode/${testMode}/date-range`,
      { params },
    );
  }

  /**
   * Get tests by student and date range
   * @param studentId - Student user ID
   * @param startDate - Optional start date
   * @param endDate - Optional end date
   * @returns Observable of API collection response
   */
  getTestsByStudentAndDateRange(
    studentId: string,
    startDate?: Date,
    endDate?: Date,
  ): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate.toISOString());
    if (endDate) params = params.set('endDate', endDate.toISOString());

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/student/${studentId}/date-range`,
      { params },
    );
  }

  /**
   * Get available tests for a student to take
   * @param studentId - Student user ID
   * @returns Observable of API collection response with available tests
   */
  /** Available tests for the authenticated student (JWT auto-scope). */
  getAvailableTestsForStudent(
    _studentId?: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/available`,
    );
  }

  /**
   * Get available tests for student by mode
   * @param studentId - Student user ID
   * @param testMode - Test mode
   * @returns Observable of API collection response
   */
  /** Available tests by mode for the authenticated student (JWT auto-scope). */
  getAvailableTestsForStudentByMode(
    _studentId: string,
    testMode: string,
  ): Observable<ApiCollectionResponse<Test>> {
    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.studentTestsUrl}/available/mode/${testMode}`,
    );
  }

  /**
   * Calculate test score for a session
   * @param sessionId - Test session ID
   * @returns Observable with score result
   */
  calculateTestScore(
    sessionId: number,
  ): Observable<ApiSingleResponse<TestScoreResultDto>> {
    return this.http.post<ApiSingleResponse<TestScoreResultDto>>(
      `${this.studentTestsUrl}/sessions/${sessionId}/calculate-score`,
      {},
    );
  }

  /**
   * Get student progression (overall)
   * @param studentId - Student user ID
   * @returns Observable with progression data
   */
  getStudentProgression(
    studentId: string,
  ): Observable<ApiSingleResponse<StudentTestProgressionDto>> {
    return this.http.get<ApiSingleResponse<StudentTestProgressionDto>>(
      `${this.studentTestsUrl}/progression/${studentId}`,
    );
  }

  /**
   * Get student progression by mode
   * @param studentId - Student user ID
   * @param testMode - Test mode
   * @returns Observable with progression data filtered by mode
   */
  getStudentProgressionByMode(
    studentId: string,
    testMode: string,
  ): Observable<ApiSingleResponse<StudentTestProgressionDto>> {
    return this.http.get<ApiSingleResponse<StudentTestProgressionDto>>(
      `${this.studentTestsUrl}/progression/${studentId}/mode/${testMode}`,
    );
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get themes by IDs
   * @param ids - Array of theme IDs
   * @returns Observable with themes
   */
  getThemesByIds(ids: number[]): Observable<Theme[]> {
    return this.http.post<Theme[]>(`${this.baseUrl}/questions/themes`, ids);
  }

  // ==================== STATE MANAGEMENT METHODS ====================

  /**
   * Fetch tests by professor and update state
   * @param userId - Professor user ID
   */
  fetchTests(userId: string): void {
    this.getByTestsProfessorId(userId).subscribe({
      next: (res: ApiCollectionResponse<Test>) => {
        if (res.status === 'SUCCESS') {
          this.testsSubject.next(res.data);
        }
      },
      error: (err) => {
        this.errorHandler.handle('SWAL.FETCH_TESTS_ERROR');
      },
    });
  }

  /**
   * Fetch tests by professor and subject, update state
   * @param userId - Professor user ID
   * @param subjectId - Subject ID
   */
  fetchProfessorTestsBySubject(userId: string, subjectId: number): void {
    this.getByTestsProfessorSubject(userId, subjectId).subscribe({
      next: (res: ApiCollectionResponse<Test>) => {
        if (res.status === 'SUCCESS') {
          this.testsSubject.next(res.data);
        }
      },
      error: (err) => {
        this.errorHandler.handle('SWAL.FETCH_TESTS_ERROR');
      },
    });
  }

  /**
   * Fetch test with questions and update state
   * @param testId - Test ID
   */
  fetchTestWithQuestions(testId: number): void {
    this.getTestWithQuestions(testId).subscribe({
      next: (res: ApiSingleResponse<Test>) => {
        if (res.status === 'SUCCESS') {
          this.testWithQuestionsSubject.next(res.data);
        }
      },
      error: (err) => {
        this.errorHandler.handle('SWAL.FETCH_TESTS_ERROR');
        this.testWithQuestionsSubject.next(null);
      },
    });
  }

  /**
   * Clear tests state
   */
  clearTests(): void {
    this.testsSubject.next([]);
  }

  /**
   * Clear test with questions state
   */
  clearTestWithQuestions(): void {
    this.testWithQuestionsSubject.next(null);
  }

  /**
   * Placeholder function for modifing exam status
   */
  /**
   * Tests en attente de validation.
   *
   * La file de relecture ne montre que des questions : sans cette liste, un
   * test importé y reste invisible.
   */
  getPendingReviewTests(subjectId?: number): Observable<ApiCollectionResponse<Test>> {
    let params = new HttpParams();
    if (subjectId) params = params.set('subjectId', subjectId);

    return this.http.get<ApiCollectionResponse<Test>>(
      `${this.baseUrl}/professor/tests/pending-review`,
      { params },
    );
  }

  /**
   * Approuve un test et toutes ses questions en attente, d'un seul geste.
   *
   * Destiné aux tests importés : approuver plusieurs dizaines de questions une
   * à une décourage la relecture. Une question sans bonne réponse reste en
   * brouillon et remonte dans les avertissements.
   */
  approveTest(
    testId: number,
    options: { comment?: string; publish?: boolean } = {},
  ): Observable<ApiSingleResponse<TestReviewResult>> {
    return this.http.post<ApiSingleResponse<TestReviewResult>>(
      `${this.baseUrl}/professor/tests/${testId}/approve`,
      { comment: options.comment ?? null, publish: options.publish ?? false },
    );
  }

  /** Renvoie un test et ses questions en correction. Le motif est obligatoire. */
  rejectTest(
    testId: number,
    comment: string,
  ): Observable<ApiSingleResponse<TestReviewResult>> {
    return this.http.post<ApiSingleResponse<TestReviewResult>>(
      `${this.baseUrl}/professor/tests/${testId}/reject`,
      { comment },
    );
  }

  updateStatus(
    testId: number,
    status: 'draft' | 'published'
  ): Observable<ApiSimpleResponse> {
    return this.http.patch<ApiSimpleResponse>(
      `${this.baseUrl}/professor/tests/${testId}/publish`,
      { status }
    );
  }
}
